/* eslint-disable max-lines */

import { Customer, UUID } from '@litlingo/client';
import { collapseCategory, expandCategory } from 'actions';
import LinkLookup from 'components/LinkLookup';
import { ARROW_CLOSE_ENVELOPE } from 'constants/envelopeIcons';
import { startCase } from 'lodash';
import pluralize from 'pluralize';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getModelCategories } from 'selectors/models';
import { getExpandedCategories } from 'selectors/useCasePage';
import { useSelector } from 'store';
import logEvent from 'utils/analytics';
import { printStringArray } from 'utils/strings';
import { groupedCategoryByRule } from '.';
import CustomerListItem from './CustomerListItem';

type RouteParams = {
  modelId: UUID;
};

type ComponentProps = {
  ruleCategory: groupedCategoryByRule;
  selected: boolean;
  selectedModels: string[];
  setSelectedModels: React.Dispatch<React.SetStateAction<string[]>>;
  selectedCustomer: string;
  setSelectedCustomer: React.Dispatch<React.SetStateAction<string>>;
};

const CategoryListItem: React.FC<ComponentProps> = ({
  ruleCategory,
  selected,
  selectedModels,
  setSelectedModels,
  selectedCustomer,
  setSelectedCustomer,
}) => {
  const dispatch = useDispatch();
  const { modelId } = useParams<RouteParams>();

  const expandedCategories = useSelector(getExpandedCategories);
  const allCategories = useSelector((state) => getModelCategories(state, modelId));

  const [expanded, setExpanded] = useState(expandedCategories.includes(ruleCategory.uuid));

  const customers = ruleCategory.categories.reduce<Record<string, Customer>>((acc, cur) => {
    if (cur.customer) {
      return { ...acc, [cur.customer_uuid]: cur.customer };
    }
    return acc;
  }, {});

  const getCategoryState = (): string[] => {
    const categoryState = [];
    if (
      allCategories.some(
        // @ts-ignore
        (c) => c.rule?.rule_group_uuid === ruleCategory.uuid && c.tenant_name === 'human'
      )
    ) {
      categoryState.push('Production');
    }
    if (
      allCategories.some(
        // @ts-ignore
        (c) => c.rule?.rule_group_uuid === ruleCategory.uuid && c.tenant_name === 'modeler'
      )
    ) {
      categoryState.push('Staging');
    }
    return categoryState;
  };

  const expandedCustomerRow = Object.values(customers)
    .sort((a, b) => {
      if (a.name && b.name && a.name > b.name) {
        return 1;
      }
      if (a.uuid > b.uuid) {
        return 1;
      }
      return -1;
    })
    .map((c) => (
      <CustomerListItem
        key={c.uuid}
        ruleCategory={ruleCategory}
        customer={c}
        selectedModels={selectedModels}
        setSelectedCustomer={setSelectedCustomer}
        selected={selectedCustomer === `${ruleCategory.uuid}_${c.uuid}_${c.domain}`}
      />
    ));

  const handleModelSelect = (checked: boolean): void => {
    if (checked) {
      setSelectedModels((prev) => [...prev, ruleCategory.uuid]);
    } else {
      setSelectedModels((prev) => prev.filter((id) => id !== ruleCategory.uuid));
    }
  };

  const handleRowCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.stopPropagation();
    handleModelSelect(e.target.checked);
  };

  return (
    <>
      <LinkLookup
        routeName="global-rule-group-manager"
        routeParams={{ ruleId: ruleCategory.categories[0].rule?.rule_group_uuid || '' }}
        className="border-t border-litlingo-gray-1 hover:bg-litlingo-gray-0.5 table-row"
        onClick={(): Promise<void> => logEvent('single-use-case')}
        as="tr"
      >
        <td className="table-wrapper__new-td h-12 flex w-full">
          <div className="w-full px-2 h-full flex items-center justify-end">
            <input
              id={`checkbox-user-${ruleCategory.uuid}`}
              data-testid={`checkbox-${ruleCategory.name}`}
              type="checkbox"
              className="cursor-pointer form-checkbox litlingo-checkbox w-5 h-5"
              onClick={(e): void => e.stopPropagation()}
              onChange={(e): void => {
                handleRowCheckboxChange(e);
              }}
              checked={selected}
              disabled={!!selectedCustomer}
            />
          </div>
        </td>

        <td className="table-wrapper__new-td h-12">
          <div className="flex items-center text-body pr-4">
            <div className="flex flex-row gap-2 items-center overflow-hidden">
              <button
                className="flex items-center h-full focus:outline-none"
                type="button"
                data-testid="expand-button"
                onClick={(e): void => {
                  e.stopPropagation();
                  if (expanded) {
                    dispatch(collapseCategory(ruleCategory.uuid));
                  } else {
                    dispatch(expandCategory(ruleCategory.uuid));
                  }
                  setExpanded(!expanded);
                }}
              >
                <span
                  className={`focus:outline-none transition-transform duration-300 ${
                    expanded && 'transform rotate-90'
                  }`}
                >
                  {ARROW_CLOSE_ENVELOPE()}
                </span>
              </button>
              <div className="truncate font-bold">{ruleCategory.name}</div>
            </div>
          </div>
        </td>
        <td className="table-wrapper__new-td">
          <div className="flex items-center text-body">
            <div className="overflow-hidden pr-4">
              <div className="truncate">{ruleCategory.description}</div>
            </div>
          </div>
        </td>

        <td className="table-wrapper__new-td">
          <div className="flex items-center text-body">
            <div className="overflow-hidden">
              <div className="truncate">{`${Object.keys(customers).length} ${pluralize(
                'Customer',
                Object.keys(customers).length
              )}`}</div>
            </div>
          </div>
        </td>

        <td className="table-wrapper__new-td">
          <div className="flex items-center text-body">
            <div className="overflow-hidden">
              <div className="truncate">{printStringArray(getCategoryState())}</div>
            </div>
          </div>
        </td>

        <td className="table-wrapper__new-td">
          <div className="flex items-center text-body">
            <div className="overflow-hidden pr-4">
              <div className="truncate">
                {startCase(
                  // @ts-ignore
                  ruleCategory.categories.filter((c) => c.tenant_name === 'modeler')[0]?.updated_by
                    .name || ''
                )}
              </div>
            </div>
          </div>
        </td>
      </LinkLookup>
      {expanded && expandedCustomerRow}
    </>
  );
};

export default CategoryListItem;
